/**
 * Function to compute n!
 * n! = n x n - 1 x n - 2 x n - 3...x 2 x 1
 * @param n - Number to compute its factorial
 * @returns {number} n!
 */
export function factorial(n: number): number{
  if(n < 2){
    return 1;
  }

  return n * factorial(n-1);
}

/**
 * Function to find the number of permutations(arrangements) of r in n items
 * P(n, r) = n! / (n - r)!;
 * @param n - Number of items from which to make arrangements
 * @param r - Number of items per arrangement
 * @returns {number} - Number of permutations(arrangements)
 */
export function P(n: number, r: number): number{
  if(n < r){
     // Edge case: If selecting more than available items
    return 0;
  }

  return factorial(n) / factorial(n-r);
}

/**
 * Function to find the number of combinations(selections) of r in n items
 * C(n, r) = n! / (r! x (n - r)!) = P(n, r) / r!;
 * @param n - Number of items from which to make selections
 * @param r - Number of items per selection
 * @returns {number} Number of combinations(selections)
 */
export function C(n: number, r: number): number{
    if(n < r){
       // Edge case: If selecting more than available items
      return 0;
    }

    return P(n, r) / factorial(r);
}

/**
 * Function to compute the number of unit bets of a game for a simple (SI)
 * @param selectionsCount - Number of selections made for the game
 * @param gameBase - Number of selections required in a unit bet of that game
 * @param isCF - Play Game with complete Formula (Formule Complète (CF))
 * @returns {number} Number of unit bets of a game for a simple (SI)
 */
export function SI(selectionsCount: number, gameBase: number, isCF: boolean = false): number{
  if(selectionsCount < gameBase){
    return 0; // number of selections has to be at least equal to the game base.
  }

  if(selectionsCount === gameBase){
    return isCF ? P(selectionsCount, gameBase) : 1;
  }

  // selectionsCount > gameBase
  return CE(selectionsCount, gameBase, isCF);
}

/**
 * Function to compute the number of unit bets of a game for a Combined(Champ elargie (CE))
 * @param selectionsCount - Number of selections made for the game
 * @param gameBase - Number of selections required in a unit bet of that game
 * @param isCF - Play Game with complete Formula (Formule Complète (CF))
 * @returns {number} Number of unit bets of a game for a Combined
 */
export function CE(selectionsCount: number, gameBase: number, isCF: boolean = false): number{
    if(selectionsCount <= gameBase || gameBase < 1){
      return 0; // number of selections has to be at least equal to the game base.
    }

    return isCF ? P(selectionsCount, gameBase) : C(selectionsCount, gameBase);
}

/**
 * Function to compute the number of unit bets of a game for a Reduced Field(Champ Partial (CP))
 * @param selectionsCount  - Number of selections made for the game (at most (gameBase - 1) and at least 1, for a valid CP)
 * @param gameBase - Number of selections required in a unit bet of that game
 * @param associateCount - Number of associates selected (All if no selection)
 * @param isCF - Play Game with complete Formula (Formule Complète (CF))
 * @param isOrder - Determine if order of horses counts for a valid unit bet
 * @returns {number} Number of unit bets of a game for a Reduced Field
 */
export function CP(selectionsCount: number, gameBase: number, associateCount: number, isCF: boolean = false, isOrder: boolean = false): number{
  if(selectionsCount < 0 || selectionsCount >= gameBase || (associateCount < gameBase - selectionsCount)  || gameBase < 1){
    return 0; // number of selections has to be at most (gameBase - 1).
  }

  // If no selection then is a CT
  // if(selectionsCount === 0){
  //     return CT(gameBase, associateCount, isOrder);
  // }

  // number of horse to complete the base for the game
  let x = gameBase - selectionsCount;

  let unitBetCount = isOrder ? P(associateCount, x) : C(associateCount, x);

  if(isCF){
    unitBetCount = unitBetCount * P(gameBase, selectionsCount);
  }

  if(unitBetCount <= 1)return 0;

  return unitBetCount;
}``

/**
 * Function to compute the number of unit bets of a game for a Total Field(Champ Total (CT))
 * @param selectionsCount  - Number of selections made for the game (at most (gameBase - 1) and at least 1, for a valid CT)
 * @param gameBase - Number of selections required in a unit bet of that game
 * @param associateCount - Number of associates selected (All if no selection)
 * @param poolSize - Total number of horses in the race
 * @param isCF - Play Game with complete Formula (Formule Complète (CF))
 * @param isOrder - Determine if order of horses counts for a valid unit bet
 * @returns {number} Number of unit bets of a game for a Total Field
 */
export function CT(selectionsCount: number, gameBase: number, associateCount: number, poolSize: number, isOrder: boolean = false, isCF: boolean = false): number{
  if(selectionsCount < 1 || selectionsCount >= gameBase || gameBase < 1){
    return 0; // number of associate should be at least gameBase.
  }
  // Number of horses to fill the base
  const x = gameBase - selectionsCount;
  const remaining = poolSize - selectionsCount;
  if (remaining < x) return 0;

  let totalBetCount = isOrder  ? P(remaining, x) : C(remaining, x);

  if(isCF){
    totalBetCount = totalBetCount * P(gameBase, selectionsCount);
  }

  return totalBetCount;
}
